import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, X, Loader } from 'lucide-react';

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'loading';
  timestamp: number;
  tokenMint?: string; // Add tokenMint to track toasts per token
}

// Global registry of active toasts to prevent duplicates
const activeToastRegistry: Record<string, Set<string>> = {
  loading: new Set<string>(),
  error: new Set<string>(),
  success: new Set<string>()
};

// Registry to track toasts by token mint to prevent duplicates
const tokenMintRegistry = new Map<string, string>();

const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    // Handle showing a toast
    const handleShowToast = (event: CustomEvent) => {
      if (!event.detail) return;
      
      const { message, type = 'success', id, tokenMint, uniqueId } = event.detail;
      if (!message) return;
      
      const toastId = id || uniqueId || `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      
      // If tokenMint is provided, check if we already have a toast for this token
      if (tokenMint) {
        const existingToastId = tokenMintRegistry.get(tokenMint);
        
        if (existingToastId) {
          // Update the existing toast instead of creating a new one
          setToasts(prev => prev.map(t => {
            if (t.id === existingToastId) {
              return { ...t, message, type, timestamp: Date.now() };
            }
            return t;
          }));
          return;
        }
        
        // Register this new toast with the token
        tokenMintRegistry.set(tokenMint, toastId);
      }
      
      // If this is a loading toast, ensure we only have one loading toast with this ID
      if (type === 'loading') {
        // If this exact loading toast is already active, don't add another
        if (activeToastRegistry.loading.has(toastId)) {
          return;
        }
        
        // Remove any existing loading toasts with this ID
        setToasts(prev => prev.filter(t => !(t.type === 'loading' && t.id === toastId)));
        
        // Register this loading toast
        activeToastRegistry.loading.add(toastId);
      }
      
      // For error and success, clear any duplicates with the same message
      if (type === 'error' || type === 'success') {
        if (activeToastRegistry[type].has(message) && !tokenMint) {
          return;
        }
        
        // Only track by message for general toasts (not token-specific)
        if (!tokenMint) {
          activeToastRegistry[type].add(message);
          
          // Auto-remove after a delay
          setTimeout(() => {
            activeToastRegistry[type].delete(message);
          }, 5000);
        }
      }
      
      // Add the new toast
      setToasts(prev => {
        // Remove any existing duplicates with same ID or message for non-loading toasts
        const filtered = prev.filter(t => {
          // Keep all toasts of different types
          if (t.type !== type) return true;
          
          // If dealing with a token-specific toast, remove any others for this token
          if (tokenMint && t.tokenMint === tokenMint) return false;
          
          // For loading toasts, remove ones with the same ID
          if (type === 'loading' && t.id === toastId) return false;
          
          // For success/error toasts, remove ones with the same message (if not token-specific)
          if (!tokenMint && (type === 'success' || type === 'error') && t.message === message) return false;
          
          return true;
        });
        
        // Add the new toast
        return [...filtered, {
          id: toastId, 
          message, 
          type,
          timestamp: Date.now(),
          tokenMint
        }];
      });
      
      // Auto-dismiss success and error toasts after a delay
      if (type === 'success' || type === 'error') {
        // Use custom duration if provided, otherwise default to 4 seconds
        const dismissDelay = event.detail.duration || 4000;
        
        setTimeout(() => {
          setToasts(prev => prev.filter(t => t.id !== toastId));
          
          // Clean up token registry if needed
          if (tokenMint && tokenMintRegistry.get(tokenMint) === toastId) {
            tokenMintRegistry.delete(tokenMint);
          }
          
          // Clean up active toast registry
          if (type === 'success' && !tokenMint) {
            activeToastRegistry.success.delete(message);
          } else if (type === 'error' && !tokenMint) {
            activeToastRegistry.error.delete(message);
          }
        }, dismissDelay);
      }
    };
    
    // Handle updating an existing toast
    const handleUpdateToast = (event: CustomEvent) => {
      if (!event.detail) return;
      
      const { id, message, type } = event.detail;
      if (!id || !message) return;
      
      setToasts(prev => prev.map(toast => {
        if (toast.id === id) {
          return { ...toast, message, type: type || toast.type, timestamp: Date.now() };
        }
        return toast;
      }));
    };
    
    // Handle hiding a toast
    const handleHideToast = (event: CustomEvent) => {
      if (!event.detail) return;
      
      const { id, type } = event.detail;
      if (!id) return;
      
      // First find the toast to get its tokenMint if present
      const toastToRemove = toasts.find(t => t.id === id);
      
      // Remove from registry if it's a loading toast
      if (type === 'loading') {
        activeToastRegistry.loading.delete(id);
      }
      
      // Remove from token registry if needed
      if (toastToRemove?.tokenMint) {
        if (tokenMintRegistry.get(toastToRemove.tokenMint) === id) {
          tokenMintRegistry.delete(toastToRemove.tokenMint);
        }
      }
      
      // Remove the toast from state
      setToasts(prev => prev.filter(t => t.id !== id));
    };

    // Handle dismissing a toast (same as hiding)
    const handleDismissToast = (event: CustomEvent) => {
      // Use the same logic as hide toast
      handleHideToast(event);
    };
    
    // Handle clearing all toasts of a specific type
    const handleClearToasts = (event: CustomEvent) => {
      if (!event.detail) return;
      
      const { type } = event.detail;
      
      if (type) {
        // First find all toasts of this type to clean up token registry
        toasts.forEach(toast => {
          if (toast.type === type && toast.tokenMint) {
            tokenMintRegistry.delete(toast.tokenMint);
          }
        });
        
        // Clear all toasts of a specific type
        setToasts(prev => prev.filter(t => t.type !== type));
        
        // Clear the registry for this type
        if (type === 'loading') activeToastRegistry.loading.clear();
        if (type === 'error') activeToastRegistry.error.clear();
        if (type === 'success') activeToastRegistry.success.clear();
      } else {
        // Clear all toasts
        setToasts([]);
        tokenMintRegistry.clear();
        activeToastRegistry.loading.clear();
        activeToastRegistry.error.clear();
        activeToastRegistry.success.clear();
      }
    };
    
    // Register event listeners
    window.addEventListener('ekko:show_toast', handleShowToast as EventListener);
    window.addEventListener('ekko:update_toast', handleUpdateToast as EventListener);
    window.addEventListener('ekko:hide_toast', handleHideToast as EventListener);
    window.addEventListener('ekko:dismiss_toast', handleDismissToast as EventListener);
    window.addEventListener('ekko:clear_toasts', handleClearToasts as EventListener);
    
    // Safety cleanup - periodically check for stale toasts
    const safetyInterval = setInterval(() => {
      const now = Date.now();
      setToasts(prev => {
        // Find toasts to remove
        const toRemove: string[] = [];
        
        // Filter out stale toasts
        const filtered = prev.filter(toast => {
          // Remove loading toasts older than 30 seconds
          if (toast.type === 'loading' && now - toast.timestamp > 30000) {
            // Track for removal from registries
            toRemove.push(toast.id);
            
            // Also clean up token registry if needed
            if (toast.tokenMint && tokenMintRegistry.get(toast.tokenMint) === toast.id) {
              tokenMintRegistry.delete(toast.tokenMint);
            }
            
            return false;
          }
          
          // Remove success/error toasts older than 6 seconds (safety fallback)
          if ((toast.type === 'success' || toast.type === 'error') && now - toast.timestamp > 6000) {
            console.log(`🧹 Safety cleanup: Removing stale ${toast.type} toast after 6 seconds`);
            
            // Track for removal from registries
            toRemove.push(toast.id);
            
            // Clean up token registry if needed
            if (toast.tokenMint && tokenMintRegistry.get(toast.tokenMint) === toast.id) {
              tokenMintRegistry.delete(toast.tokenMint);
            }
            
            // Clean up active toast registry
            if (!toast.tokenMint) {
              if (toast.type === 'success') {
                activeToastRegistry.success.delete(toast.message);
              } else if (toast.type === 'error') {
                activeToastRegistry.error.delete(toast.message);
              }
            }
            
            return false;
          }
          
          return true;
        });
        
        // Clean up toast registry
        toRemove.forEach(id => {
          activeToastRegistry.loading.delete(id);
        });
        
        return filtered;
      });
    }, 2000); // Check every 2 seconds for more responsive cleanup
    
    return () => {
      window.removeEventListener('ekko:show_toast', handleShowToast as EventListener);
      window.removeEventListener('ekko:update_toast', handleUpdateToast as EventListener);
      window.removeEventListener('ekko:hide_toast', handleHideToast as EventListener);
      window.removeEventListener('ekko:dismiss_toast', handleDismissToast as EventListener);
      window.removeEventListener('ekko:clear_toasts', handleClearToasts as EventListener);
      clearInterval(safetyInterval);
    };
  }, [toasts]);
  
  // No toasts, no render
  if (toasts.length === 0) return null;
  
  // Sort toasts by time (newest first) and limit to 3 
  const visibleToasts = [...toasts]
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 3);
  
  // Get background color based on toast type
  const getBgColor = (type: string) => {
    switch (type) {
      case 'success': return 'bg-[#16161a] border-[#24c35b]';
      case 'error': return 'bg-[#16161a] border-[#ef4444]';
      case 'loading': return 'bg-[#16161a] border-[#24c35b]';
      default: return 'bg-[#16161a] border-[#23232a]';
    }
  };
  
  // Get icon based on toast type
  const getIcon = (type: string) => {
    switch (type) {
      case 'success': return <CheckCircle className="text-[#24c35b]" size={16} />;
      case 'error': return <AlertCircle className="text-[#ef4444]" size={16} />;
      case 'loading': return <Loader className="text-[#24c35b] animate-spin" size={16} />;
      default: return null;
    }
  };
  
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {visibleToasts.map(toast => (
        <div 
          key={toast.id}
          className={`px-4 py-3 rounded-md shadow-lg backdrop-blur-md border flex items-center gap-2 min-w-[250px] max-w-[350px] text-white ${getBgColor(toast.type)}`}
          data-toast-id={toast.id}
          data-token-mint={toast.tokenMint}
        >
          {getIcon(toast.type)}
          <p className="text-sm flex-1">{toast.message}</p>
          <button 
            onClick={() => {
              // Remove from registry if needed
              if (toast.type === 'loading') {
                activeToastRegistry.loading.delete(toast.id);
              } else if (toast.type === 'error' && !toast.tokenMint) {
                activeToastRegistry.error.delete(toast.message);
              } else if (toast.type === 'success' && !toast.tokenMint) {
                activeToastRegistry.success.delete(toast.message);
              }
              
              // Clean up token registry if needed
              if (toast.tokenMint && tokenMintRegistry.get(toast.tokenMint) === toast.id) {
                tokenMintRegistry.delete(toast.tokenMint);
              }
              
              // Remove from state
              setToasts(prev => prev.filter(t => t.id !== toast.id));
            }}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
};

export default ToastContainer; 